"use client";

import { startTransition, useEffect, useState } from "react";
import { useRouter } from "next/navigation";

type Router = Pick<ReturnType<typeof useRouter>, "push" | "replace">;
type Navigate = Router["push"];

// The page does not paint while a view transition waits for its new page, so a slower page changes without the fade.
export const PAGE_WAIT_LIMIT = 1000;

export const FLIGHT_NAME = "re-photo";
// A photo this much of the window wide, at least, is the new page's big photo.
const LARGE_PHOTO = 0.4;

const modified = (event: MouseEvent) =>
  event.button !== 0 ||
  event.metaKey ||
  event.ctrlKey ||
  event.shiftKey ||
  event.altKey;

/**
 * The address of the website's other page that an unmodified primary click on a link goes to; undefined where the
 * browser keeps the click (another tab, a download, another website) and for the same page.
 */
function linkTarget(event: MouseEvent, win: Window): string | undefined {
  if (modified(event)) return undefined;
  const anchor = (event.target as Element | null)?.closest?.("a[href]");
  const target = anchor?.getAttribute("target");
  if (
    !anchor ||
    anchor.hasAttribute("download") ||
    (target && target !== "_self")
  )
    return undefined;
  const url = new URL(anchor.getAttribute("href")!, win.location.href);
  const here = new URL(win.location.href);
  if (
    url.origin !== here.origin ||
    (url.pathname === here.pathname && url.search === here.search)
  )
    return undefined;
  return url.href;
}

/**
 * The photo of the card around the clicked link, or of the link itself in a section; a link outside the sections, such
 * as the header's logo, has none.
 */
function cardPhoto(event: MouseEvent): HTMLElement | null {
  const anchor = (event.target as Element | null)?.closest?.("a[href]");
  const card =
    anchor?.closest("[data-re-card]") ??
    (anchor?.closest("[data-re-motion]") ? anchor : null);
  return card?.querySelector<HTMLElement>("[data-re-card-photo], img") ?? null;
}

/** The photo a card's photo flies to on the new page: one marked for it, else the first large photo in view. */
export function flightTarget(doc: Document, win: Window): HTMLElement | null {
  const marked = doc.querySelector<HTMLElement>("[data-re-flight-photo]");
  if (marked) return marked;
  return (
    [...doc.querySelectorAll<HTMLElement>("section img")].find((photo) => {
      const { top, width } = photo.getBoundingClientRect();
      return top < win.innerHeight && width >= win.innerWidth * LARGE_PHOTO;
    }) ?? null
  );
}

const crossfades = (win: Window) =>
  typeof win.document.startViewTransition === "function" &&
  !win.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * While a click on a link to another page of the website is dispatched, the router's push or replace to that page runs
 * in document.startViewTransition, which waits until the function handed to `committed` is called. `committed` runs in
 * the same React transition as the navigation, so a state update there commits with the new page. Next.js 15.1 has no
 * other hook into the navigation its Link starts. Every other navigation, such as a filter changing the address, runs as
 * before. With `flight` the photo of the clicked card flies to the new page's big photo (flightTarget), as one shared
 * element of the transition. Returns the cleanup.
 */
export function crossfadeLinks(
  win: Window & typeof globalThis,
  router: Router,
  committed: (done: () => void) => void,
  { flight = false }: { flight?: boolean } = {},
): () => void {
  const { push, replace } = router;
  let clicked: string | undefined;
  let photo: HTMLElement | null = null;
  const onClick = (event: MouseEvent) => {
    clicked = linkTarget(event, win);
    photo = clicked && flight ? cardPhoto(event) : null;
    if (clicked)
      win.setTimeout(() => {
        clicked = undefined;
        photo = null;
      });
  };
  const crossfading =
    (navigate: Navigate): Navigate =>
    (href, options) => {
      if (
        !clicked ||
        new URL(href, win.location.href).href !== clicked ||
        !crossfades(win)
      )
        return navigate(href, options);
      const from = photo;
      let to: HTMLElement | null = null;
      clicked = undefined;
      photo = null;
      if (from) from.style.viewTransitionName = FLIGHT_NAME;
      const transition = win.document.startViewTransition(
        () =>
          new Promise<void>((done) => {
            let captured = false;
            const finish = () => {
              captured = true;
              done();
            };
            win.setTimeout(finish, PAGE_WAIT_LIMIT);
            startTransition(() => {
              navigate(href, options);
              committed(() => {
                if (from) {
                  // A name may be on one element only when the browser captures the new page; once it has, a name
                  // set now would stay after the transition.
                  from.style.viewTransitionName = "";
                  if (!captured) {
                    to = flightTarget(win.document, win);
                    if (to === from) to = null;
                    if (to) to.style.viewTransitionName = FLIGHT_NAME;
                  }
                }
                finish();
              });
            });
          }),
      );
      const land = () => {
        if (to) to.style.viewTransitionName = "";
      };
      transition?.finished?.then(land, land);
    };
  router.push = crossfading(push);
  router.replace = crossfading(replace);
  win.addEventListener("click", onClick, true);
  return () => {
    win.removeEventListener("click", onClick, true);
    router.push = push;
    router.replace = replace;
  };
}

/**
 * »Skift mellem sider« at »Toner over« on a look-system website: a link to another page of it crossfades to that page;
 * with `flight` (»Billedet flyver med«) a card's photo flies along.
 */
export default function PageTransition({
  flight = false,
}: {
  flight?: boolean;
}) {
  const router = useRouter();
  const [committed, setCommitted] = useState<() => void>();
  useEffect(() => {
    committed?.();
  }, [committed]);
  useEffect(
    () =>
      crossfadeLinks(window, router, (done) => setCommitted(() => done), {
        flight,
      }),
    [router, flight],
  );
  return null;
}
